import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, ACCOUNT_COLORS } from "@/lib/api";

const KEYS = ["1","2","3","4","5","6","7","8","9","","0","DEL"];

/**
 * ClaimProfileModal — Tactical Brutalism "Claim Your Profile" flow.
 *
 * Shown when a user taps a profile that has NO PIN set yet. Lets that
 * person assign their own 4-digit PIN, which then locks the account to
 * them. Calls POST /api/users/{id}/set-pin and on success invokes
 * onClaimed(updatedUser) so the parent can persist device-binding and
 * navigate into the dashboard.
 */
export default function ClaimProfileModal({ user, open, onClose, onClaimed }) {
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [stage, setStage] = useState("enter"); // "enter" | "confirm"
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setPin("");
      setConfirm("");
      setStage("enter");
      setError("");
      setBusy(false);
    }
  }, [open]);

  const active = stage === "enter" ? pin : confirm;
  const setActive = stage === "enter" ? setPin : setConfirm;

  const tap = (k) => {
    setError("");
    if (k === "DEL") return setActive((p) => p.slice(0, -1));
    if (!k) return;
    setActive((p) => (p.length < 4 ? p + k : p));
  };

  useEffect(() => {
    if (stage === "enter" && pin.length === 4) {
      setStage("confirm");
    }
  }, [pin, stage]);

  useEffect(() => {
    if (stage !== "confirm" || confirm.length !== 4 || busy) return;
    if (pin !== confirm) {
      setError("PINS DON'T MATCH — TRY AGAIN");
      setStage("enter");
      setPin("");
      setConfirm("");
      return;
    }
    (async () => {
      setBusy(true);
      try {
        const updated = await api.setPlayerPin(user.id, pin);
        onClaimed?.(updated);
      } catch (err) {
        const detail = err?.response?.data?.detail || "";
        setError(detail.toUpperCase() || "COULDN'T CLAIM PROFILE");
        setStage("enter");
        setPin("");
        setConfirm("");
      } finally {
        setBusy(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirm, stage, busy, pin, user, onClaimed]);

  if (!user) return null;
  const color = ACCOUNT_COLORS[user.account_type] || "#fff";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-md bg-[var(--stakes-surface)] border-2 border-[var(--neon-pink)] rounded-none p-0 [&>button]:hidden"
        data-testid="claim-profile-modal"
        style={{ boxShadow: "0 0 32px rgba(255,20,147,0.25), inset 0 0 60px rgba(255,20,147,0.05)" }}
      >
        <DialogTitle className="sr-only">Claim profile for {user.name}</DialogTitle>
        <DialogDescription className="sr-only">Set your 4-digit PIN</DialogDescription>

        <div className="p-6 md:p-8 border-b-2 border-[var(--neon-pink)]">
          <div className="label-mono" style={{ color: "var(--neon-pink)" }}>CLAIM PROFILE</div>
          <div className="font-anton text-4xl md:text-5xl uppercase leading-none mt-1" data-testid="claim-profile-name">
            {user.name}
          </div>
          <div className="label-mono mt-3" style={{ color }}>
            SET YOUR 4-DIGIT PIN — ONLY YOU WILL KNOW IT.
          </div>

          <div className="label-mono mt-5" data-testid="claim-stage-label">
            {stage === "enter" ? "STEP 1 OF 2 · NEW PIN" : "STEP 2 OF 2 · CONFIRM PIN"}
          </div>
          <div className="flex gap-3 mt-3" data-testid="claim-pin-display">
            {[0,1,2,3].map((i) => (
              <div
                key={i}
                className="flex-1 h-16 border-2 flex items-center justify-center font-anton text-4xl"
                style={{
                  borderColor: i < active.length ? "var(--neon-pink)" : "var(--stakes-border)",
                  background: i < active.length ? "rgba(255,20,147,0.06)" : "transparent",
                }}
              >
                {i < active.length ? "●" : ""}
              </div>
            ))}
          </div>
          {error && (
            <div className="mt-4 label-mono" style={{ color: "var(--stakes-primary)" }} data-testid="claim-error">
              {error}
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 bg-[var(--stakes-border)] gap-px">
          {KEYS.map((k, idx) => (
            <button
              key={idx}
              disabled={busy || !k}
              onClick={() => tap(k)}
              data-testid={k ? `claim-numpad-${k.toLowerCase()}` : `claim-numpad-empty-${idx}`}
              className={`h-20 bg-[var(--stakes-surface)] font-barlow font-bold text-3xl uppercase tracking-wider hover-flip ${
                k && k !== "DEL"
                  ? "hover:bg-white hover:text-black"
                  : k === "DEL"
                    ? "hover:bg-[var(--neon-pink)]"
                    : "cursor-default"
              }`}
            >
              {k}
            </button>
          ))}
        </div>
        <div className="p-4 border-t-2 border-[var(--stakes-border)] flex items-center justify-between">
          <div className="label-mono text-[var(--stakes-muted)]">
            {busy ? "CLAIMING…" : "AUTO-ADVANCES ON 4 DIGITS"}
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            className="label-mono hover:text-white disabled:opacity-40"
            data-testid="claim-cancel-button"
          >
            ← CANCEL
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
